import {
  Box,
  Button,
  formatDateTime,
  FullPageSpinner,
  HStack,
  IconButton,
  Text,
  VStack,
  WhiteCard,
} from '@elkaso-app/web-design';
import { useGetCreditUtilizationApi } from 'apis/bnpl';
import { useEffect } from 'react';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { Can } from 'configs/can';
import { ApproveUtilizationModal } from 'pages/bnpl/components/approve-utilization-modal';
import { BnplStatusBadge } from 'pages/bnpl/components/bnpl-status-badge';
import { DeclineUtilizationModal } from 'pages/bnpl/components/decline-utilization-modal';
import { formatAmount } from 'pages/bnpl/utils/bnpl-statuses';
import { FiArrowLeft } from 'react-icons/fi';
import { useHistory, useParams } from 'react-router-dom';

const InfoItem = ({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) => (
  <Box minW='150px'>
    <Text fontSize='xs' color='gray.500'>
      {label}
    </Text>
    {children || <Text fontWeight='medium'>{value || '---'}</Text>}
  </Box>
);

export const CreditUtilizationDetailScreen = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { isLoading, data: utilizationData } = useGetCreditUtilizationApi(id);

  useEffect(() => {
    dispatch(changeTitle('BNPL - Credit Utilization'));
  }, [dispatch]);
  const utilization = utilizationData?.data;

  if (isLoading) return <FullPageSpinner />;
  if (!utilization) return <Text p='lg'>Utilization not found</Text>;

  return (
    <VStack spacing='lg' align='stretch' p='lg'>
      {/* Summary Card */}
      <WhiteCard p='lg'>
        <HStack justify='space-between' mb='lg'>
          <HStack spacing='md'>
            <IconButton
              aria-label='back-to-utilizations'
              icon={<FiArrowLeft />}
              variant='ghost'
              size='sm'
              onClick={() => history.push('/app/bnpl/credit-utilizations')}
            />
            <Text fontSize='xl' fontWeight='bold'>
              Credit Utilization #{utilization.id}
            </Text>
            <BnplStatusBadge status={utilization.utilizationStatus} type='utilization' />
          </HStack>

          {utilization.utilizationStatus === 'pending' && (
            <Can I='manage' a='CreditUtilization'>
              <HStack spacing='sm'>
                <ApproveUtilizationModal
                  trigger={
                    <Button size='sm' colorScheme='green'>
                      Approve
                    </Button>
                  }
                  utilizationId={utilization.id}
                />
                <DeclineUtilizationModal
                  trigger={
                    <Button size='sm' variant='outline' colorScheme='red'>
                      Decline
                    </Button>
                  }
                  utilizationId={utilization.id}
                />
              </HStack>
            </Can>
          )}
        </HStack>

        <HStack spacing='xl' flexWrap='wrap'>
          <InfoItem label='Restaurant' value={utilization.creditFacility?.restaurant?.nameEn} />
          <InfoItem label='Supplier' value={utilization.creditFacility?.supplier?.name} />
          <InfoItem label='Facility ID' value={String(utilization.facilityId)} />
          <InfoItem label='Source Type' value={utilization.sourceType === 'invoice' ? 'Invoice' : 'Statement'} />
          <InfoItem label='Source ID' value={String(utilization.sourceId)} />
          <InfoItem label='Amount' value={formatAmount(utilization.amount)} />
          <InfoItem label='Notes' value={utilization.notes || '---'} />
        </HStack>
      </WhiteCard>

      {/* Audit Info */}
      <WhiteCard p='lg'>
        <Text fontSize='lg' fontWeight='bold' mb='md'>
          Audit Info
        </Text>
        <HStack spacing='xl' flexWrap='wrap'>
          <InfoItem label='Created By' value={String(utilization.createdBy)} />
          <InfoItem label='Created At' value={formatDateTime(utilization.createdAt)} />
          {utilization.approvedAt && (
            <>
              <InfoItem label='Approved By' value={String(utilization.approvedBy)} />
              <InfoItem label='Approved At' value={formatDateTime(utilization.approvedAt)} />
            </>
          )}
          {utilization.declinedAt && (
            <>
              <InfoItem label='Declined By' value={String(utilization.declinedBy)} />
              <InfoItem label='Declined At' value={formatDateTime(utilization.declinedAt)} />
            </>
          )}
        </HStack>
      </WhiteCard>
    </VStack>
  );
};
